import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { apiFetch } from '@/lib/api/client';
import { publicHostFromLocation } from '@/lib/config';
import { getTemplateRenderer } from '@/features/editor/preview/templates';
import type { PreviewDraft } from '@/features/editor/preview/LivePreview';
import type { PortfolioSectionKind, TemplateId } from '@/types/portfolio';

interface PublicConfig {
  readonly tenantId: string;
  readonly subdomain: string;
  readonly customDomain: string | null;
  readonly portfolio: {
    readonly template: TemplateId;
    readonly theme: string;
    readonly fontPair: string;
    readonly enabledSections: ReadonlyArray<PortfolioSectionKind>;
    readonly published: PreviewDraft;
    readonly publishedAt: string;
  };
  readonly owner: { readonly name: string; readonly avatarUrl: string | null };
}

export function PublicPortfolioPage() {
  const { subdomain } = useParams<{ subdomain: string }>();
  const hostFromLocation = publicHostFromLocation();
  const host = subdomain ?? hostFromLocation ?? '';
  const query = useQuery({
    queryKey: ['public-config', host],
    queryFn: () =>
      apiFetch<PublicConfig>('/public/config', {
        searchParams: { host },
        skipAuth: true,
      }),
    enabled: host.length > 0,
    staleTime: 60_000,
  });

  const tenantId = query.data?.tenantId;
  useEffect(() => {
    if (!tenantId) return;
    recordPageview(tenantId);
  }, [tenantId]);

  if (query.isPending) {
    return <Shell title="Loading portfolio…" />;
  }
  if (query.isError) {
    return (
      <Shell title="Portfolio not found">
        <p className="text-sm text-ink-muted">
          No published portfolio at this address yet.
        </p>
      </Shell>
    );
  }

  const { portfolio, owner } = query.data;
  const enabled = new Set<PortfolioSectionKind>(portfolio.enabledSections);
  const Renderer = getTemplateRenderer(portfolio.template);

  return (
    <div className="min-h-screen bg-canvas text-ink">
      <Renderer draft={portfolio.published} enabled={enabled} ownerName={owner.name || 'Portfolio'} />
    </div>
  );
}

function recordPageview(tenantId: string): void {
  try {
    apiFetch('/public/pageview', {
      method: 'POST',
      body: {
        tenantId,
        path: window.location.pathname || '/',
        referrer: document.referrer || undefined,
      },
      skipAuth: true,
    }).catch(() => {
      /* beacon is best-effort; swallow to keep the page silent */
    });
  } catch {
    /* noop */
  }
}

function Shell({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-canvas px-6 text-ink">
      <h1 className="font-display text-2xl font-semibold tracking-tight">{title}</h1>
      {children ? <div className="mt-3 text-center">{children}</div> : null}
    </div>
  );
}
